<template>
  <div :class="$style.container">
    <transition :name="$style.longFade" mode="out-in">
      <LoadingPage v-if="!isCheckedAuthorization || !isReceivedDefaultLanguageTranslations" />
      <div v-else :class="$style.content">
        <router-view />
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'

import { useRoute, useRouter } from 'vue-router'
import { useAuthorizationStore } from '@stores/authorizationStore'
import { useUserStore } from '@stores/userStore'
import { useLocalizationStore } from '@stores/localizationStore'
import { useSelectStore } from '@stores/selectStore'

import { LanguageList, type LanguageType } from '@models/entities/languages'

import { queryController } from '@composables/queryController'

import { isAvailableLocalStorage } from '@scripts/helpers/windowHelpers'
import { checkLanguageInLists } from '@scripts/entities/languages'
import { createLanguageTranslationsRequestUrl } from '@scripts/helpers/appHelpers'

import { APP_LANGUAGES } from '@definitions/languages'

import LoadingPage from '@pages/loading/LoadingPage.vue'

const router = useRouter()
const route = useRoute()
const authStore = useAuthorizationStore()
const userStore = useUserStore()
const localizationStore = useLocalizationStore()
const selectStore = useSelectStore()

const { getQueryByKey } = queryController()

const availableAppLanguages = computed<LanguageType[]>(() => localizationStore.getAvailableLanguages)
const isReceivedAvailableLanguages = computed(() => localizationStore.isReceivedAvailableLanguages)
const isReceivedDefaultLanguageTranslations = computed(() => localizationStore.isReceivedDefaultLanguageTranslations)
const isCheckedAuthorization = computed(() => authStore.isCheckedAuthorization)
const isReadyToInitLanguages = computed(() => isReceivedAvailableLanguages.value && isCheckedAuthorization.value)


const initAppLanguage = async (isAuthorized: boolean) => {
  const localStorageLanguage: LanguageType | string | null =
    isAvailableLocalStorage() ? localStorage.getItem('lang') : null

  if (isAuthorized) {
    if (userStore.getUserLanguage && checkLanguageInLists(userStore.getUserLanguage, [APP_LANGUAGES, availableAppLanguages.value])) {
      localizationStore.setAppLanguage(userStore.getUserLanguage)
    } else if (localStorageLanguage && checkLanguageInLists(localStorageLanguage, [APP_LANGUAGES, availableAppLanguages.value])) {
      localizationStore.setAppLanguage(localStorageLanguage as LanguageType)
      await userStore.postUserInfo({ lang: localStorageLanguage as LanguageType })
    } else {
      localizationStore.setAppLanguage(LanguageList.EN)
      await userStore.postUserInfo({ lang: LanguageList.EN })
    }
  } else {
    const queryLanguage: string[] | undefined = getQueryByKey<string>('lang')

    if (queryLanguage && queryLanguage.length && checkLanguageInLists(queryLanguage[0], [APP_LANGUAGES, availableAppLanguages.value])) {
      localizationStore.setAppLanguage(queryLanguage[0] as LanguageType)
    } else if (localStorageLanguage && checkLanguageInLists(localStorageLanguage, [APP_LANGUAGES, availableAppLanguages.value])) {
      localizationStore.setAppLanguage(localStorageLanguage as LanguageType)
    } else {
      localizationStore.setAppLanguage(LanguageList.EN)
    }
  }
}

const getCurrentLanguageTranslations = async () => {
  const url = createLanguageTranslationsRequestUrl(localizationStore.getAppLanguage)
  await localizationStore.getDefaultLanguageTranslations(url)
}

watch(() => authStore.isAuthorized, (newVal) => {
  if (authStore.routeNameForAuthorizedStatusSwitching) {
    router.push({ name: authStore.routeNameForAuthorizedStatusSwitching, query: newVal ? route.query : undefined })
  } else {
    selectStore.reset()
  }
})

watch(() => isReceivedDefaultLanguageTranslations.value, (newVal) => {
  if (newVal) {
    localizationStore.getAllAvailableLanguagesTranslations()
  }
})

watch(() => isReadyToInitLanguages.value, (newVal) => {
  if (newVal) {
    initAppLanguage(authStore.isAuthorized)
    getCurrentLanguageTranslations()
  }
})
</script>

<style lang="scss" module>
@use "@styles/transitions/transitions.scss";

.container {
  @include flex-column-center();
  width: 100%;
  height: calc(100dvh - env(safe-area-inset-top));
}

@media print {
  .container {
    height: auto;
  }
}

.content {
  @include size('100%');
  @include flex-column();
  overflow: hidden;
}

.nav {
  @include flex-row-center(8);
  padding-top: 20px;

  & > div {
    width: 100px;
    padding: 8px 12px;
    border-radius: 4px;
    background-color: var(--Brand-Default-Default);
    color: var(--Stable-Color-White);
    text-align: center;
    cursor: pointer;

    &:hover {
      @media (hover: hover) and (pointer: fine) {
        background-color: var(--Brand-Default-Hover);
      }
    }
  }
}
</style>
